/**
 * O painel de cadastro — **a peça reaproveitável**.
 *
 * Foi o pedido literal: *"tem que ser um componente à parte, pra que ele possa
 * ser utilizado dentro dos outros sistemas"*. Ele aparece em três lugares e é o
 * mesmo componente nos três:
 *
 *  1. na aba de Cadastros, como painel da direita;
 *  2. dentro do editor de diagrama, como cartão flutuante;
 *  3. a partir do "+" do campo de busca do lançamento de pinout.
 *
 * O que muda entre eles é o **esquema** (qual entidade) e o **hospedeiro** (onde
 * ele é desenhado). Por isso ele não sabe nada sobre gaveta, modal ou cartão:
 * recebe o esquema, o registro e os dois retornos, e desenha o formulário.
 *
 * Cadastro é painel, nunca modal — regra 2 da padronização.
 */

import { useEffect, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import type { Campo, Esquema } from "./esquemas"
import { vazio } from "./esquemas"
import { ConferenciaDoCircuito, type EstadoDoNome } from "./ConferenciaDoCircuito"
import {
  chaveModelosDaRestricao, gravarModelosDaRestricao, ModelosDaRestricao, NovoModelo, usarModelosDaRestricao,
} from "./ModelosDaRestricao"

/** `id` é o do registro gravado — o painel precisa dele para gravar o que vem junto (os modelos da restrição). */
export type ResultadoDeSalvar = { ok: true; id?: number } | { ok: false; erro: unknown }

export function PainelDeCadastro({
  esquema, registro, salvando, aoSalvar, aoCancelar, aoExcluir, excluindo, acima, aoTransferir, projetoId,
}: {
  esquema: Esquema
  /**
   * O projeto do registro. Com ele, o circuito tem o nome conferido enquanto se
   * digita (a mesma conferência do editor de ligações): nome de outro circuito é
   * recusado, e o que o nome passa a puxar pelo começo pede confirmação.
   */
  projetoId?: number
  /** Uma linha de contexto sobre o título — de quem é este registro. */
  acima?: string
  /** `null` = incluindo. Um objeto = editando. */
  registro: Record<string, unknown> | null
  salvando: boolean
  aoSalvar: (dados: Record<string, unknown>) => Promise<ResultadoDeSalvar>
  aoCancelar: () => void
  aoExcluir?: () => void
  excluindo?: boolean
  /**
   * Mesclar este registro noutro. Só aparece quando o hospedeiro oferece — o
   * painel é a peça reaproveitável, e transferir não faz sentido em todo lugar
   * que ele é usado (no gráfico, por exemplo, não há lista para escolher o
   * destino).
   */
  aoTransferir?: () => void
}) {
  const incluindo = registro == null
  const [dados, setDados] = useState<Record<string, unknown>>(() => registro ?? vazio(esquema))
  const [erro, setErro] = useState<unknown>(null)
  const [camposComErro, setCamposComErro] = useState<Record<string, string>>({})
  const primeiro = useRef<HTMLInputElement>(null)
  const confereNome = esquema.chave === "circuitos" && (projetoId ?? 0) > 0
  const [nomeConferido, setNomeConferido] = useState<EstadoDoNome>({ bloqueio: null, confirmar: false })
  const [nomeConfirmado, setNomeConfirmado] = useState(false)

  // A restrição grava também os modelos a que se aplica — os checkboxes do legado.
  const qc = useQueryClient()
  const comModelos = esquema.chave === "restricoes" && (projetoId ?? 0) > 0
  const restricaoId = comModelos ? Number(registro?.id ?? 0) : 0
  const modelosGravados = usarModelosDaRestricao(restricaoId)
  const [modelos, setModelos] = useState<Set<number> | null>(null)
  const [erroDosModelos, setErroDosModelos] = useState<string | undefined>()
  // O painel de incluir modelo, aberto no lugar deste — mesma área, mesma largura.
  const [incluindoModelo, setIncluindoModelo] = useState(false)
  useEffect(() => {
    setErroDosModelos(undefined)
    if (!comModelos) return
    if (restricaoId === 0) setModelos(new Set())
    else setModelos(modelosGravados.data ? new Set(modelosGravados.data) : null)
  }, [comModelos, restricaoId, modelosGravados.data])

  // Trocar de registro (ou de aba) recarrega o formulário e apaga o erro antigo.
  useEffect(() => {
    setDados(registro ?? vazio(esquema))
    setErro(null)
    setCamposComErro({})
    setNomeConfirmado(false)
    setIncluindoModelo(false)
    primeiro.current?.focus()
  }, [registro, esquema])

  function mudar(nome: string, valor: unknown) {
    setDados((d) => ({ ...d, [nome]: valor }))
    if (nome === "descricao") setNomeConfirmado(false)
    // O erro do campo some assim que a pessoa mexe nele: manter a marca vermelha
    // enquanto ela corrige é ruído.
    setCamposComErro((c) => (c[nome] ? { ...c, [nome]: "" } : c))
  }

  const faltando = esquema.campos.filter(
    (c) => c.obrigatorio && String(dados[c.nome] ?? "").trim() === "",
  )

  async function salvar() {
    if (faltando.length > 0) {
      setCamposComErro(Object.fromEntries(faltando.map((c) => [c.nome, "Obrigatório."])))
      return
    }
    if (confereNome && nomeConferido.bloqueio) {
      setCamposComErro({ descricao: nomeConferido.bloqueio })
      return
    }
    if (confereNome && nomeConferido.confirmar && !nomeConfirmado) {
      setCamposComErro({ descricao: "Confirme abaixo que o fio também liga nos circuitos listados." })
      return
    }
    if (comModelos && modelosGravados.isError) {
      setErroDosModelos("Os modelos desta restrição não carregaram — gravar agora apagaria os que já estão ligados.")
      return
    }
    if (comModelos && (modelos?.size ?? 0) === 0) {
      setErroDosModelos("Escolha ao menos um modelo.")
      return
    }
    setErro(null)
    const r = await aoSalvar(dados)
    if (r.ok && comModelos) {
      const id = r.id ?? restricaoId
      try {
        await gravarModelosDaRestricao(projetoId!, id, [...modelos!])
        qc.invalidateQueries({ queryKey: chaveModelosDaRestricao(id) })
      } catch (e) {
        setErro(e)
      }
      return
    }
    if (!r.ok) {
      setErro(r.erro)
      if (r.erro instanceof ErroDaApi && r.erro.campos.length > 0) {
        setCamposComErro(Object.fromEntries(r.erro.campos.map((c) => [c.campo, c.texto])))
      }
    }
  }

  return (
    <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
    <form
      onSubmit={(e) => { e.preventDefault(); void salvar() }}
      onKeyDown={(e) => {
        // Ctrl+Enter grava de qualquer campo, Esc fecha — regra 3.
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void salvar() }
        if (e.key === "Escape") { e.preventDefault(); aoCancelar() }
      }}
      style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <div
        style={{
          height: 44, flex: "0 0 44px", display: "flex", alignItems: "center", gap: 8,
          padding: "0 14px", borderBottom: "1px solid var(--line)",
        }}
      >
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {acima && (
            <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".02em", color: "var(--txt3)" }}>
              {acima}
            </span>
          )}
          <span style={{ fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {incluindo
              ? `Nov${esquema.artigo === "a" ? "a" : "o"} ${esquema.singular}`
              : String(registro?.[esquema.titulo] ?? esquema.singular)}
          </span>
        </span>
        <button type="button" onClick={aoCancelar} aria-label="Fechar o painel"
                style={{ display: "flex", color: "var(--txt3)", padding: 4 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14 }}>
        <div className="grade-formulario" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {esquema.campos.map((c, i) => (
            <CampoDoFormulario
              key={c.nome}
              campo={c}
              valor={dados[c.nome]}
              erro={camposComErro[c.nome]}
              aoMudar={(v) => mudar(c.nome, v)}
              refPrimeiro={i === 0 ? primeiro : undefined}
            />
          ))}
        </div>

        {comModelos && (
          <ModelosDaRestricao
            projetoId={projetoId!}
            restricaoId={restricaoId}
            marcados={modelos}
            aoMudar={(m) => { setModelos(m); setErroDosModelos(undefined) }}
            erro={erroDosModelos}
            falhouAoCarregar={modelosGravados.isError}
            aoNovoModelo={() => setIncluindoModelo(true)}
          />
        )}

        {confereNome && (
          <ConferenciaDoCircuito
            projetoId={projetoId!}
            circuitoId={Number(registro?.id ?? 0)}
            nome={String(dados.descricao ?? "")}
            original={registro ? String(registro.descricao ?? "") : null}
            confirmado={nomeConfirmado}
            aoConfirmar={setNomeConfirmado}
            aoMudarEstado={setNomeConferido}
          />
        )}

        {esquema.nota && (
          <p style={{
            margin: "14px 0 0", padding: "9px 11px", borderRadius: 8, fontSize: 11.5,
            lineHeight: 1.5, background: "var(--surface2)", color: "var(--txt2)",
          }}>
            {esquema.nota}
          </p>
        )}

        {erro != null && <Erro erro={erro} />}
      </div>

      <div
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
          padding: 12, borderTop: "1px solid var(--line)",
        }}
      >
        {!incluindo && aoTransferir && (
          <button
            type="button"
            onClick={aoTransferir}
            title="Mescla este registro noutro"
            style={{
              height: 32, padding: "0 11px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
              color: "var(--txt2)", border: "1px solid var(--line2)",
            }}
          >
            Transferir
          </button>
        )}

        {!incluindo && aoExcluir && (
          <button
            type="button"
            onClick={aoExcluir}
            disabled={excluindo}
            style={{
              height: 32, padding: "0 11px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
              color: "var(--erro)", border: "1px solid var(--line2)",
            }}
          >
            {excluindo ? "Excluindo…" : "Excluir"}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={aoCancelar}
                style={{ height: 32, padding: "0 12px", borderRadius: 7, fontSize: 12.5, color: "var(--txt2)" }}>
          Cancelar
        </button>
        <button
          type="submit"
          disabled={salvando || (confereNome && nomeConferido.bloqueio !== null)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 13px",
            borderRadius: 7, background: "var(--brand)", color: "#FFFFFF",
            fontSize: 12.5, fontWeight: 600,
            opacity: salvando || (confereNome && nomeConferido.bloqueio !== null) ? 0.6 : 1,
          }}
        >
          {salvando ? "Salvando…" : incluindo ? "Incluir" : "Salvar"}
          <kbd style={{ fontSize: 10, opacity: 0.8 }}>Ctrl ↵</kbd>
        </button>
      </div>
    </form>

    {incluindoModelo && comModelos && (
      <div style={{ position: "absolute", inset: 0, zIndex: 5, display: "flex", background: "var(--surface)" }}>
        <NovoModelo
          projetoId={projetoId!}
          aoFechar={() => setIncluindoModelo(false)}
          // Criou: volta para a restrição com o modelo novo já marcado.
          aoCriar={(id) => {
            setModelos((m) => new Set([...(m ?? []), id]))
            setErroDosModelos(undefined)
            setIncluindoModelo(false)
          }}
        />
      </div>
    )}
    </div>
  )
}

function CampoDoFormulario({
  campo, valor, erro, aoMudar, refPrimeiro,
}: {
  campo: Campo
  valor: unknown
  erro?: string
  aoMudar: (v: unknown) => void
  refPrimeiro?: React.RefObject<HTMLInputElement>
}) {
  const borda = erro ? "var(--erro)" : "var(--line2)"
  const comum: React.CSSProperties = {
    width: "100%", height: 32, padding: "0 9px", borderRadius: 7,
    border: `1px solid ${borda}`, background: "var(--surface2)", fontSize: 12.5,
    fontFamily: campo.mono ? "var(--fm)" : undefined,
  }

  return (
    <label style={{ gridColumn: campo.largura === 2 ? "span 2" : "span 1", display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
        {campo.rotulo}
        {campo.obrigatorio && <span style={{ color: "var(--erro)", marginLeft: 3 }}>*</span>}
      </span>

      {campo.tipo === "lista" ? (
        <select value={Number(valor ?? 0)} onChange={(e) => aoMudar(Number(e.target.value))} style={comum}>
          {(Array.isArray(campo.opcoes) ? campo.opcoes : Object.entries(campo.opcoes ?? {})).map(([v, r]) => (
            <option key={v} value={v}>{r || "(vazio)"}</option>
          ))}
        </select>
      ) : campo.tipo === "area" ? (
        <textarea
          value={String(valor ?? "")}
          onChange={(e) => aoMudar(e.target.value)}
          rows={3}
          style={{ ...comum, height: "auto", padding: "7px 9px", resize: "vertical", lineHeight: 1.45 }}
        />
      ) : (
        <input
          ref={refPrimeiro}
          type={campo.tipo === "numero" ? "number" : "text"}
          value={campo.tipo === "numero" ? Number(valor ?? 0) : String(valor ?? "")}
          onChange={(e) => aoMudar(campo.tipo === "numero" ? Number(e.target.value) : e.target.value)}
          placeholder={campo.dica}
          style={comum}
        />
      )}

      {erro && <span style={{ fontSize: 11, color: "var(--erro)" }}>{erro}</span>}
      {!erro && campo.ajuda && (
        <span style={{ fontSize: 11, color: "var(--txt3)", lineHeight: 1.4 }}>{campo.ajuda}</span>
      )}
    </label>
  )
}

/**
 * O erro do salvamento. Três casos com tratamentos diferentes, e o primeiro é o
 * que mais aparece em desenvolvimento.
 */
function Erro({ erro }: { erro: unknown }) {
  // A trava do canário: mensagem inteira, sem traduzir. Em desenvolvimento isso
  // tem que doer — é o aviso de que a escrita ia para um projeto real.
  if (erro instanceof ErroDeTrava) {
    return (
      <div style={{
        marginTop: 14, padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
        background: "var(--acao-soft)", color: "var(--acao-on-soft)",
        border: "1px solid var(--acao)",
      }}>
        <b style={{ display: "block", marginBottom: 3 }}>Bloqueado pela trava do canário</b>
        {erro.message}
      </div>
    )
  }

  // 409: vínculo que impede a operação. A lista importa mais que a frase.
  if (erro instanceof ErroDaApi && erro.ehConflito) {
    return (
      <div style={{
        marginTop: 14, padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
        background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
      }}>
        <b style={{ display: "block", marginBottom: 3 }}>Está em uso</b>
        {erro.message}
      </div>
    )
  }

  const texto = erro instanceof ErroDaApi ? erro.message : "Não foi possível salvar."
  return (
    <div style={{
      marginTop: 14, padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
      background: "var(--erro-soft)", color: "var(--erro-on-soft)",
    }}>
      {texto}
    </div>
  )
}
